import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRight, X, Send, Sparkles } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

const ENQUIRY_TYPES = [
  'Admissions',
  'Careers',
  'Parent Query / Feedback / Complaint',
  'Transport Helpdesk',
  'ID Card Helpdesk',
  'General'
];

const AdmissionEnquiryTab = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  if (location.pathname === '/parent-login') return null;

  const onSubmit = (e) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => alert('Thank you! Our admissions team will reach out shortly.'), 200);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open admission enquiry drawer"
        className="hidden md:flex fixed right-0 top-1/2 -translate-y-1/2 z-40 items-center gap-2 bg-accent text-ink font-bold tracking-[0.32em] text-[11px] uppercase px-3 py-5 rounded-l-2xl shadow-[0_10px_30px_-8px_rgba(197,120,99,0.7)] hover:shadow-[0_18px_50px_-8px_rgba(197,120,99,0.95)] hover:px-4 transition-all duration-300"
        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
      >
        <ArrowRight size={14} style={{ transform: 'rotate(-90deg)' }} />
        Admission Enquiry
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-[60] bg-olive-footer/60 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="fixed right-0 top-0 bottom-0 z-[61] w-full sm:w-[420px] bg-olive-light shadow-[0_0_60px_rgba(0,0,0,0.35)] flex flex-col"
              role="dialog"
              aria-modal="true"
              aria-labelledby="enquiry-title"
            >
              {/* Header */}
              <div className="relative bg-olive-deep text-white px-6 py-5 flex items-center justify-between">
                <div className="absolute inset-0 bg-grid-olive [background-size:24px_24px] opacity-25" />
                <div className="relative flex items-center gap-3">
                  <img src="/ttss_logo.png" alt="TTSS" className="h-9 w-auto drop-shadow" />
                  <div>
                    <div id="enquiry-title" className="font-display text-lg font-bold leading-tight">Enquire Now</div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-accent/90 mt-0.5">TTSS · Admissions</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 hover:bg-accent hover:text-ink text-white transition"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={onSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
                <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.3em] text-olive">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inset-0 rounded-full bg-accent animate-ping opacity-80" />
                    <span className="relative rounded-full h-1.5 w-1.5 bg-accent" />
                  </span>
                  Quick Enquiry
                </div>

                <div>
                  <label className="block text-sm font-semibold text-olive-deep mb-1.5">
                    Name <span className="text-accent">*</span>
                  </label>
                  <input
                    required
                    type="text"
                    placeholder="Parent / Guardian name"
                    className="w-full rounded-lg border border-olive/30 bg-white px-3.5 py-2.5 text-sm focus:border-olive focus:outline-none focus:ring-[3px] focus:ring-olive/15 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-olive-deep mb-1.5">
                    Phone <span className="text-accent">*</span>
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-olive/30 bg-olive/5 text-olive-deep text-sm font-semibold">+91</span>
                    <input
                      required
                      type="tel"
                      pattern="[0-9]{10}"
                      placeholder="99999 99999"
                      className="flex-1 rounded-r-lg border border-olive/30 bg-white px-3.5 py-2.5 text-sm focus:border-olive focus:outline-none focus:ring-[3px] focus:ring-olive/15 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-olive-deep mb-1.5">Email</label>
                  <input
                    type="email"
                    placeholder="you@email.com"
                    className="w-full rounded-lg border border-olive/30 bg-white px-3.5 py-2.5 text-sm focus:border-olive focus:outline-none focus:ring-[3px] focus:ring-olive/15 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-olive-deep mb-1.5">Enquiry Type</label>
                  <select className="w-full rounded-lg border border-olive/30 bg-white px-3.5 py-2.5 text-sm focus:border-olive focus:outline-none focus:ring-[3px] focus:ring-olive/15 transition">
                    {ENQUIRY_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-olive-deep mb-1.5">Message</label>
                  <textarea
                    rows={3}
                    placeholder="Tell us a bit about your enquiry..."
                    className="w-full rounded-lg border border-olive/30 bg-white px-3.5 py-2.5 text-sm focus:border-olive focus:outline-none focus:ring-[3px] focus:ring-olive/15 transition resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="group relative w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-accent text-ink font-bold text-sm uppercase tracking-[0.16em] shadow-[0_14px_36px_-10px_rgba(197,120,99,0.7)] hover:shadow-[0_20px_50px_-10px_rgba(197,120,99,1)] hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-accent via-accent-soft to-accent bg-[length:200%_100%] animate-gradient-shift opacity-0 group-hover:opacity-100 transition-opacity" />
                  <Sparkles size={14} className="relative" />
                  <span className="relative">Send Message</span>
                  <Send size={14} className="relative transition-transform group-hover:translate-x-1" />
                </button>

                <p className="text-[11px] text-ink/55 leading-relaxed text-center">
                  By submitting you agree to be contacted by the TTSS admissions team. Your information is never shared.
                </p>
              </form>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdmissionEnquiryTab;
